import { useParams } from 'react-router-dom'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  ProcessSidePanelPoliciesDocument,
  ProcessesProcessCategoryEnum,
} from 'generated/resources/graphql'

import FormRow from 'modules/core/components/FormRow'
import Select from 'modules/core/components/Select'
import Sidepanel from 'modules/core/components/Sidepanel'
import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useForm } from 'modules/core/hooks/useForm'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useCreateProcessFromTemplate } from 'modules/processes/hooks/useCreateProcessFromTemplate'
import type { Process } from 'modules/processes/hooks/useProcess'
import { useUpdateProcess } from 'modules/processes/hooks/useUpdateProcess'
import { useValidateStringLength } from 'modules/tasks/hooks/useValidateStringLength'

type Props = {
  process?: Process
  onClose: () => void
  onSuccess: (processId: string) => void
}

interface ProcessDescriptionFormValues {
  name: string
  nameError: string | undefined
  description: string | undefined
  category: ProcessesProcessCategoryEnum | undefined | null
}

export enum StepProgressionCondtion {
  Manual = 'manual',
  Automatic = 'automatic',
  ByDate = 'by_date',
}

const DEFAULT = 'default'
const ProcessSidePanel = ({ onSuccess, process, onClose }: Props) => {
  const isNewProcess = !process?.id
  const { createProcess, loading: creating } = useCreateProcessFromTemplate()
  const { updateProcess, loading: updating } = useUpdateProcess()
  const { validate, isValid } = useValidateStringLength()
  const { category: templateCategory } = useParams()

  // Fetch training access policies
  const { data: policiesData } = useQuery(ProcessSidePanelPoliciesDocument)
  const canAccessTrainings =
    policiesData?.trainings._policies.trainingsAccessManagementSpace.can ?? false

  const loading = creating || updating

  const allCategoryOptions = [
    { value: 'custom', label: i18n.t('processes.common.tag.custom') },
    {
      value: 'onboarding',
      label: i18n.t('processes.common.tag.onboarding'),
    },
    {
      value: 'offboarding',
      label: i18n.t('processes.common.tag.offboarding'),
    },
    { value: 'training', label: i18n.t('processes.common.tag.training') },
  ]

  // Filter out 'training' category if user doesn't have access
  const categoryOptions = allCategoryOptions.filter(
    (option) => option.value !== 'training' || canAccessTrainings
  )

  const { values, setValues, setErrors, handleSubmit } = useForm<ProcessDescriptionFormValues>({
    defaultValues: {
      name: process?.name ?? '',
      nameError: undefined,
      description: process?.description ?? '',
      category: process?.category ?? (templateCategory as ProcessesProcessCategoryEnum), // eslint-disable-line no-type-assertion/no-type-assertion
    },
    onSubmit,
  })

  useUnsafeEffect(() => {
    if (process) {
      setValues({
        name: process.name,
        nameError: undefined,
        description: process.description ?? '',
        category: process.category ?? undefined,
      })
      validate(process.name)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [process, setValues])

  const disabled = loading || !isValid

  const onCreate = async (values: ProcessDescriptionFormValues) => {
    if (!values.category) throw new Error('Category should be defined at this point')

    const result = await createProcess({
      templateId: templateId(values.category),
      name: values.name,
      description: values.description,
      category: values.category,
    })

    return handleResult(result, (errors) => {
      if (errors) setErrors(errors)
    })
  }

  const onUpdate = async (values: ProcessDescriptionFormValues) => {
    const result = await updateProcess({
      id: String(process?.id),
      name: values.name,
      description: values.description,
      category: values.category,
    })
    return handleResult(result, (errors) => {
      if (errors) setErrors(errors)
    })
  }

  async function onSubmit(values: ProcessDescriptionFormValues) {
    const handled = isNewProcess ? await onCreate(values) : await onUpdate(values)

    if (handled.success && handled.data.process) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })

      onSuccess(handled.data.process?.id)
    }
    return handled
  }

  function onChange(type: keyof ProcessDescriptionFormValues) {
    return function (value: string) {
      setValues({ ...values, [type]: value })
    }
  }

  const changeName = (value: string) => {
    const error = validate(value)
    setValues({ ...values, name: value, nameError: error })
  }

  const workflowNameCategory =
    templateCategory === 'custom'
      ? i18n.t('processes.update_process.workflow_name')
      : templateCategory

  return (
    <Sidepanel
      open
      overlay
      boxPadding
      width="wide"
      title={values.name}
      onClose={onClose}
      headerBorder
      closeLabel={i18n.t('common.close')}
      footer={
        <>
          <Button onClick={onClose} type="secondary">
            {i18n.t('common.cancel')}
          </Button>
          <Button onClick={() => handleSubmit()} disabled={disabled}>
            {isNewProcess
              ? i18n.t('processes.update_process.create')
              : i18n.t('processes.update_process.save_changes')}
          </Button>
        </>
      }
    >
      <FormRow>
        <Box gap="s8" paddingY="s12">
          <Text.Normal size="300" weight="semibold" capitalize>
            {i18n.t('processes.update_process.process_name', { category: workflowNameCategory })}
          </Text.Normal>
        </Box>
        <TextField
          mandatory
          type="text"
          placeholder={i18n.t('processes.update_process.name_placeholder')}
          value={values.name}
          error={values.nameError}
          isNotValid={values.nameError ? !isValid : false}
          onChange={changeName}
        />
      </FormRow>
      <FormRow>
        <Box gap="s8" paddingY="s12">
          <Text.Normal size="300" weight="semibold">
            {i18n.t('processes.update_process.description')}
          </Text.Normal>
        </Box>
        <TextArea
          placeholder={i18n.t('processes.update_process.description')}
          value={values.description}
          onChange={onChange('description')}
        />
      </FormRow>
      <FormRow>
        <FormRow>
          <Box gap="s8" paddingY="s12">
            <Text.Normal size="300" weight="semibold">
              {i18n.t('processes.update_process.category')}
            </Text.Normal>
          </Box>

          <Select
            value={values.category ? String(values.category) : undefined}
            onChange={onChange('category')}
            options={categoryOptions}
          />
        </FormRow>
      </FormRow>
    </Sidepanel>
  )
}

function templateId(category: string) {
  if (category === 'training') return 'training_certificate'
  return DEFAULT
}

export default ProcessSidePanel
